/* eslint-disable max-len */
import React, {
    useState, useRef, useEffect, useCallback, useMemo
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useModal } from '@jutro/components';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import appConfig from 'app-config';
import { ViewModelForm, useDataRefresh, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import { FormattedMessage, useTranslator } from '@jutro/locale';
import { MLVehicleInfoLookupService } from 'ml-services-common-react';
import { MLDriverVehicleUtil, MLNotificationBlockUtil } from 'ml-portals-util-react';
import { MLAlertGroupComponent } from 'ml-components-common-react';
import { FormModalPopover, platformMessages } from 'ml-components-platform-react';
import messages from './MLPartialVehicleDetailsComponent.messages';
import styles from './MLPartialVehicleDetailsComponent.module.scss';
import metadata from './MLPartialVehicleDetailsComponent.metadata.json5';
import agentMetadata from './MLPartialVehicleDetailsComponent.agent.metadata.json5';
import SelectVehiclePopupComponent from "./SelectVehiclePopupComponent/SelectVehiclePopupComponent"
import SelectVehiclePopupMessage from "./SelectVehiclePopupComponent/SelectVehiclePopupComponent.messages"
import { MLProductCodeUtil } from 'ml-portals-util-js';

// MLPartialVehicleDetailsCtrl, ml-partial-vehicle-details.html
// MLPAEditVechicle.js, ml-pa-edit-vehicle.html
// used for ml-pa-add-vehicle-dialog.html (QnB, PE), ml-pa-vehicles.html (PE PC)
function MLPartialVehicleDetailsComponent(props) {
    const modalApi = useModal();
    const {
        id = 'mlpartialvehicledetails',
        onValidate,
        showValidationErrors,
        // reset banner message in FormModalPopover
        setBannerMessage,
        //
        viewModel,
        submissionVM,
        showProgressDialog,
        hideProgressDialog,
        updateModifiers,
        isCompRaterQuote = false,
        initialAlerts = [],
        // show CostNew, comprehensiveSymbol, collisionSymbol, value
        // required in EditVehiclePage but not on PreFill AddVehicle popup
        showAdditionalFields = false,
        isPolicyChange = false,
        isNewVehicleTab,
        viewModelService,
        updateWizardData,
        updateVinErrorCheck,
        vinErrorCheck,
        setFarmUseField
    } = props;

    const {
        onValidate: setComponentValidation,
        isComponentValid
    } = useValidation(id);



    const { authHeader } = useAuthentication();
    const { refreshData } = useDataRefresh();

    const vehicleVM = viewModel;

    const isGPA = appConfig.portalCode === 'GPA';
    const isAMP = appConfig.portalCode === 'AMP';
    const isQnB = appConfig.portalCode === 'QB';
    const VIN_LENGTH = 17;
    const vehicleList = _.get(submissionVM, 'lobData.personalAuto.coverables.vehicles.children', []);
    const bodyTypelist = viewModelService.productMetadata.get('pc').types.getTypelist('BodyType');

    const [vehicleTypes, updateVehicleTypes] = useState();
    const [vehicleCategories, updateVehicleCategories] = useState();
    const [years, updateYears] = useState([]);
    const [availableMakes, updateAvailableMakes] = useState([]);
    const [availableModels, updateAvailableModels] = useState([]);
    const [isUnlistedModel, setIsUnlistedModel] = useState(false);
    const [availableBodyTypes, updateAvailableBodyTypes] = useState([]);
    const [availableTrims, updateAvailableTrims] = useState([]);
    const [vinLiveRegionMessage, setVinLiveRegionMessage] = useState('');

    const [readOnly, setReadOnly] = useState(false);
    const vehicleType = _.get(vehicleVM, 'type_ML.value.code');
    const [validVIN, setValidVIN] = useState(false);
    const [vinModified, setVinModified] = useState(false);
    const [noYearFromVerifyVin, updateNoYearFromVerifyVin] = useState(false);
    const [searchVehicleWithVin, updateSearchVehicleWithVin] = useState(!!_.get(vehicleVM, 'vin.value', false));
    const [isModifierSyncEnabled, setIsModifierSyncEnabled] = useState(true);
    const [yymResponse, setYymResponse] = useState(null);
    const [vinError, updateVinError] = useState(undefined);
    const [customAlerts, updateCustomAlerts] = useState([...initialAlerts]);
    const [priorVinInput, setPriorVinInput] = useState(null);
    const [isTrailerOrCamper, setIsTrailerOrCamper] = useState(false);
    const [showOwnedLessThan1Year, setShowOwnedLessThan1Year] = useState(false);
    const isModernProduct = _.get(submissionVM, "isModernProduct_Ext.value", false);
    const isModernProductPersonalAuto = isModernProduct && MLProductCodeUtil.isPersonalAuto(_.get(submissionVM, 'baseData.productCode.value'));
    const verifyVinInProgress = useRef(false);

    const {
        mapVinResponseToVehicle,
        hasErrorCodes,
        getErrorDescription,
        clearVinFields,
        hasCompCollSymbolsNoYear,
        isPropertyDefinedAndNotEmpty,
        createLookupCompCollRequest,
        isValidYMMRequest,
        getModelCode
    } = MLDriverVehicleUtil(viewModelService);

    const translator = useTranslator();
    const {
        clearBlockAlerts,
    } = MLNotificationBlockUtil();
    const clearVehicleErrorMessage = () => {
        if (vehicleVM.value.error) {
            vehicleVM.value.error.message = undefined
        }
        updateVinError(undefined);
    }

    const writeValue = (value, path) => {
        _.set(vehicleVM, path, value);
        refreshData();
        updateWizardData(submissionVM);
    };

    const getQuoteID = () => {
        return isAMP || isPolicyChange ? submissionVM.jobID.value : submissionVM.quoteID.value;
    };

    const getTypeKeyDescription = (typeKeyName) => {
        return translator({
            id: typeKeyName,
            defaultMessage: typeKeyName
        });
    };

    const getBodyTypeValue = (bodyType) => {
        const { name } = bodyTypelist.getCode(bodyType);
        return getTypeKeyDescription(name);
    };

    const updateFullBodyTypeList = () => {
        updateAvailableBodyTypes(bodyTypelist.codes.map((code) => ({
            code: code.code,
            name: getTypeKeyDescription(code.name)
        })));
    };

    const getMakenameByCode = (makeCode) => {
        const filtered = availableMakes.filter((make) => make.code === makeCode);
        if (filtered.length > 0) {
            return filtered[0].name;
        }
        return null;
    };

    const getModelnameByCode = (modelname) => {
        const filtered = availableModels.filter((make) => make.code === modelname);
        if (filtered.length > 0) {
            return filtered[0].name;
        }
        return null;
    };

    const hasYear = () => !!vehicleVM.value.year;
    const isVehicleYearPre1981 = () => hasYear() && vehicleVM.value.year < 1981;
    const isVehicleYearPost1980 = () => hasYear() && vehicleVM.value.year > 1980;

    const isVehicleOfCategory = (category) => {
        return category === _.get(vehicleVM, 'category_ML.value.code', '');
    };
    const isLowSpeedVehicle = () => isVehicleOfCategory('low_speed_vehicle');
    const isRegisteredGolfCart = () => isVehicleOfCategory('registered_golf_cart');
    const isPrivatePassenger = () => isVehicleOfCategory('private_passenger_light_truck') || isVehicleOfCategory('private_passenger_np');
    const isOtherPersonallyRegistered = () => isVehicleOfCategory('Other_Personally_Registered');
    const isCollectible = () => isVehicleOfCategory('collectible');
    const isTrailer = () => isVehicleOfCategory('trailer');
    const isTrailerNp = () => isVehicleOfCategory('trailer_np');
    const isCamperNp = () => isVehicleOfCategory('camper_np');

    const isTrailerNPorCamperNp = () => {
        return isTrailerNp() || isCamperNp();
    }

    const isLowSpeedVehicleGolfCartOrTrailer = () => {
        return isTrailer() || isLowSpeedVehicle() || isRegisteredGolfCart();
    };

    const isPrivatePassengerOrCollectiblePre1981 = () => (isPrivatePassenger() || isCollectible()) && isVehicleYearPre1981();

    const hasVin = () => {
        return !!vehicleVM.value.vin;
    };

    const isFullVin = () => {
        return hasVin() && vehicleVM.vin.value.length === 17;
    };

    const isValidVin = () => {
        return validVIN && vehicleVM.isTrimVinConfirmed_Ext?.value === true;
    };

    const isVinFullAndValid = () => {
        return isFullVin() && validVIN && vehicleVM.isTrimVinConfirmed_Ext?.value === true;
    };

    const hasMake = () => {
        return !!vehicleVM.value.makeCode_ML;
    };

    const hasModel = () => {
        return !!vehicleVM.value.modelCode_ML;
    };

    const hasBodyType = () => {
        return !!vehicleVM.value.bodyType_ML;
    };

    const hasTrim = () => {
        return !!vehicleVM.value.trim_ML;
    };

    const hasCollisionSymbol = () => {
        return !!vehicleVM.value.collisionSymbol_ML;
    };

    const hasCompSymbol = () => {
        return !!vehicleVM.value.comprehensiveSymbol_ML;
    };

    const hasCostNew = () => {
        return !!vehicleVM.value.costNew && vehicleVM.value.costNew.amount > 0;
    };

    const hasVehType = () => {
        return !!vehicleVM.type_ML.value;
    }

    const hasVehCategory = () => {
        return !!vehicleVM.category_ML.value;
    }

    const canLookupVin = () => {
        return hasYear() && hasMake() && hasModel();
    };

    const isPartialVinValid = () => {
        return hasVin() && hasYear() && hasMake() && hasModel() && hasBodyType() && hasTrim() && hasCollisionSymbol() && hasCompSymbol();
    };
    
    const isVinVerified = () => _.get(vehicleVM, 'isVinVerifiedSuccessfully_ML.value', false);

    const isPrefillFullVinVerified = () => {
        return (isFullVin() && hasCollisionSymbol() && hasCompSymbol() && !vinModified);
    };

    const isCostNewVisible = () => isPrivatePassenger() && (isFullVin() ? !isValidVin() || !hasCostNew() : !isPartialVinValid());

    const costNewRequired = () => isCostNewVisible() &&
        _.isEmpty(vehicleVM?.collisionSymbol_ML?.value) &&
        _.isEmpty(vehicleVM?.comprehensiveSymbol_ML?.value) &&
        (vehicleVM?.value?.costNew?.amount === 0 ||
            vehicleVM?.value?.costNew?.amount == null ||
            _.isUndefined(vehicleVM?.value?.costNew?.amount));

    const resetBodyTypeAndTrimAvailableList = () => {
        //
        // update dropdown values
        //
        if (isVehicleYearPre1981() || isUnlistedModel) {
            updateFullBodyTypeList();
        } else {
            updateAvailableBodyTypes([]);
            updateAvailableTrims([]);
        }
    };

    const clearVehicleFieldsAfterBodyType = () => {
        _.set(vehicleVM, 'collisionSymbol_ML', undefined);
        _.set(vehicleVM, 'comprehensiveSymbol_ML', undefined);
        _.set(vehicleVM, 'costNew.amount', undefined);
    };

    const clearVehicleFieldsAfterModel = () => {
        _.set(vehicleVM, 'bodyType_ML', undefined);
        _.set(vehicleVM, 'trim_ML', undefined);
        if (isGPA) {
            clearVehicleFieldsAfterBodyType();
        }
    };

    const clearVehicleFieldsAfterMake = () => {
        _.set(vehicleVM, 'model', undefined);
        _.set(vehicleVM, 'modelCode_ML', undefined);
        clearVehicleFieldsAfterModel();
    };

    const clearVehicleFieldsAfterYear = () => {
        _.set(vehicleVM, 'make', undefined);
        _.set(vehicleVM, 'makeCode_ML', undefined);
        clearVehicleFieldsAfterMake();
    };

    const clearVehicleFields = () => {
        _.set(vehicleVM, 'type_ML', undefined);
        _.set(vehicleVM, 'year', undefined);
        _.set(vehicleVM, 'isTrimVinConfirmed_Ext.value', false);
        _.set(vehicleVM, 'serialNumber_ML', undefined);
        _.set(vehicleVM, 'compCollFilledByVinML.value', false);
        clearVehicleFieldsAfterYear();
    };

    // set this on each refresh
    // If the VIN is less then 17 digits, the cost new field should be shown.
    _.set(vehicleVM, "isCostNewRequired_ML.value", isFullVin() ? !validVIN : (!hasVin() || costNewRequired()));

    const fetchMakes = async (vehicle) => {
        if (vehicle.year.value) {
            try {
                // showProgressDialog(platformMessages.loadingMakes);
                const req = {
                    year: vehicle.year.value
                };
                const result = await MLVehicleInfoLookupService.lookupMakes(req, authHeader);
                if (!_.isEmpty(result.error)) {
                    console.log(`Fetch Makes: ${result.error}`);
                    return [];
                }

                const makeCodes = _.sortBy(
                    result.makesCodes.map((make) => ({
                        code: make.code,
                        name: make.description
                    })), 'name');
                return makeCodes;
            } finally {
                // hideProgressDialog();
            }
        }
        return [];
    };

    const fetchModels = async (vehicle) => {
        if (vehicle.year.value && vehicle.makeCode_ML.value) {
            try {
                // showProgressDialog(platformMessages.loadingModels);
                const req = {
                    year: vehicle.year.value,
                    makeCode: vehicle.makeCode_ML.value
                };
                const result = await MLVehicleInfoLookupService.lookupModels(req, authHeader);
                if (!_.isEmpty(result.error)) {
                    console.log(`Fetch Models: ${result.error}`);
                    return [];
                }

                // "models" field is null when a vehicle type is trailer, for example.
                if (!result.models) {
                    return [];
                }

                const modelCodes = _.sortBy(
                    result.models.map((model) => ({
                        code: model.code,
                        name: model.description
                    })), 'name');

                return modelCodes;
            } finally {
                // hideProgressDialog();
            }
        }
        return [];
    };

    const assignBodyTypes = (vinPrefixes) => {
        const getCleanVinBodyTypes = () => {
            return vinPrefixes.filter((prefix) => !_.isEmpty(prefix.bodyType));
        };

        if (!_.isEmpty(vinPrefixes)) {
            const cleanPrefixes = getCleanVinBodyTypes(vinPrefixes);
            const codes = [];
            cleanPrefixes.forEach((vinPrefix) => {
                if (vinPrefix.bodyType && vinPrefix.bodyType.length > 0) {
                    codes.push(vinPrefix.bodyType);
                }
            });

            const validBodyTypes = bodyTypelist.codes.filter((code) => {
                return codes.includes(code.code);
            });

            if (!_.isEmpty(validBodyTypes)) {
                updateAvailableBodyTypes(validBodyTypes.map((type) => ({
                    code: type.code,
                    name: getTypeKeyDescription(type.name)
                })));
            } else {
                updateAvailableBodyTypes([]);
            }
            // MLDriverVehicleUtil.setVinIntegrationErrorCode($scope.yymResponse.error, $scope.vehicle.value);
        } else {
            updateAvailableBodyTypes([]);
        }
    };

    const assignTrims = (vinPrefixes) => {
        const trims = [];
        if (!_.isEmpty(vinPrefixes)) {
            vinPrefixes.forEach((vinPrefix) => {
                if (trims.indexOf(vinPrefix.trim) === -1 && (vinPrefix.trim && vinPrefix.trim.length > 0)) {
                    trims.push(vinPrefix.trim);
                }
            });
            if (!_.isEmpty(trims)) {
                updateAvailableTrims(trims.map((t) => ({
                    code: t,
                    name: t
                })));
            } else {
                updateAvailableTrims([]);
            }
        } else {
            updateAvailableTrims([]);
        }
    };

    const lookupVIN = async () => {
        const vehicleYear = vehicleVM.year.value ? vehicleVM.year.value.toString() : null;

        // prefill doesn't return the codes needed for VIN lookup but it does return the display values in their places
        // if the display values are found instead of the codes, get the correct codes and proceed with lookup
        const prefillMakeTC = _.find(availableMakes, { 'name': vehicleVM.makeCode_ML.value });
        if (prefillMakeTC) {
            _.set(vehicleVM, 'makeCode_ML.value', prefillMakeTC.code);
            const prefillModelTC = _.find(await fetchModels(vehicleVM), { 'name': vehicleVM.modelCode_ML.value });
            if (prefillModelTC) {
                _.set(vehicleVM, 'modelCode_ML.value', prefillModelTC.code);
            }
        }

        const req = {
            year: vehicleYear,
            makeCode: vehicleVM.makeCode_ML.value,
            modelCode: vehicleVM.modelCode_ML.value,
            quoteID: getQuoteID(),
            vehFixedId: vehicleVM.fixedId.value
        };

        if (isValidYMMRequest(req)) {
            try {
                // METINC-12603 - need comment out showProgressDialog
                // showProgressDialog(platformMessages.loadingTrims);
                const vehicleInfo = await MLVehicleInfoLookupService.lookupVin(req, authHeader);
                // cache vehicleInfo
                if (!_.isUndefined(vehicleInfo)) {
                    setYymResponse(vehicleInfo);
                }
                assignBodyTypes(vehicleInfo.vinPrefixes);
                assignTrims(vehicleInfo.vinPrefixes);
                return vehicleInfo.vinPrefixes;
            } catch (e) {
                return [];
            } finally {
                // hideProgressDialog();
            }
        }
        return [];
    };

    const onModelInputChange = (value, path) => {
        _.set(vehicleVM, path, value);
        clearVehicleFieldsAfterModel();
        refreshData();
    }

    const onVehicleType = (value, path) => {
        _.set(vehicleVM, path, value);
        refreshData();
    }

    const onModelSelect = async (value, path) => {
        _.set(vehicleVM, path, value);
        _.set(vehicleVM, 'model', getModelnameByCode(value));
        _.set(vehicleVM, 'vin', undefined);
        clearVehicleFieldsAfterModel();
        setValidVIN(false);

        // Cascading Edits
        if (!isUnlistedModel) {
            const vinPrefixes = await lookupVIN();
            assignBodyTypes(vinPrefixes);
            assignTrims(vinPrefixes);
        }
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    };

    const onMakeSelect = async (value, path) => {
        _.set(vehicleVM, path, value);
        _.set(vehicleVM, 'make', getMakenameByCode(value));
        _.set(vehicleVM, 'vin', undefined);
        setValidVIN(false);

        // Cascading Edits
        if (!isGPA) {
            clearVehicleFieldsAfterMake();
            resetBodyTypeAndTrimAvailableList();
        }

        const modelCodes = await fetchModels(vehicleVM);
        updateAvailableModels(modelCodes);

        // Cascading Edits
        if (isGPA && hasModel()) {
            if (_.find(modelCodes, { 'code': vehicleVM.modelCode_ML.value })) {
                onModelSelect(vehicleVM.modelCode_ML.value, 'modelCode_ML.value');
            } else {
                _.set(vehicleVM, 'model', undefined);
                _.set(vehicleVM, 'modelCode_ML', undefined);
                resetBodyTypeAndTrimAvailableList();
            }
        }

        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    };

    const onVehicleTypeChange = async (value, path) => {
        _.set(vehicleVM, path, value);
    }

    const onYearSelect = async (value, path) => {
        _.set(vehicleVM, path, value);
        _.set(vehicleVM, 'vin', undefined);
        setValidVIN(false);
        const hasMakeAndModelAtStart = hasMake() && hasModel();

        if (!isGPA) {
            // Cascading Edits
            clearVehicleFieldsAfterYear();
            updateAvailableModels([]);
            resetBodyTypeAndTrimAvailableList();
        } else {
            clearVehicleFieldsAfterModel();
        }

        if ((isPrivatePassenger() || isCollectible) && value > 1980) {
            const makeCodes = await fetchMakes(vehicleVM);
            updateAvailableMakes(makeCodes);

            if (isGPA && hasMake()) {
                // Cascading Edits
                if (_.find(makeCodes, { 'code': vehicleVM.makeCode_ML.value })) {
                    await onMakeSelect(vehicleVM.makeCode_ML.value, 'makeCode_ML.value');
                    if (hasMakeAndModelAtStart && !hasModel()) {
                        // model has been cleared in onMakeSelect so clear Make as well
                        _.set(vehicleVM, 'make', undefined);
                        _.set(vehicleVM, 'makeCode_ML', undefined);
                    }
                } else {
                    clearVehicleFieldsAfterYear();
                    updateAvailableModels([]);
                    resetBodyTypeAndTrimAvailableList();
                }
            }
        }
        if (onValidate) {
            onValidate(isComponentValid, id);
        }

        refreshData();
    };

    const handleLookupVinError = (vehicleInfo) => {
        clearVinFields(vehicleVM);
        // clearModelsTrims();

        const errorMessage = vehicleInfo ? getErrorDescription(vehicleInfo) : translator(messages.vinResponseInvalidPleaseTryAgain);

        if (vehicleInfo && !_.isEmpty(vehicleInfo.error) && !_.isEmpty(vehicleInfo.error.code) && vehicleInfo.error.code === 'N') {
            _.set(vehicleVM, "value.error.message", translator(errorMessage));
            updateVinError(errorMessage);
            updateCustomAlerts([...customAlerts.filter((alert) => alert.block !== "vinError"), {
                type: "error",
                message: errorMessage,
                block: "vinError"
            }])
        } else {
            updateVinError(translator(messages.vinResponseInValid, { vin: vehicleVM.vin.value }));
            _.set(vehicleVM, "value.error.message", translator(messages.vinResponseInValid, { vin: vehicleVM.vin.value }));

            updateCustomAlerts([...customAlerts.filter((alert) => alert.block !== "vinError"), {
                type: "error",
                message: errorMessage,
                block: "vinError"
            }])
        }
    };

    const lookupCollision = async () => {
        const vehicleYear = vehicleVM.year.value ? vehicleVM.year.value.toString() : null;
        const req = createLookupCompCollRequest(
            vehicleYear,
            _.get(vehicleVM, "makeCode_ML.value"),
            _.get(vehicleVM, "modelCode_ML.value"),
            getQuoteID(),
            _.get(vehicleVM, "type_ML.value.code"),
            _.get(vehicleVM, "category_ML.value.code"),
            _.get(vehicleVM, "comprehensiveSymbol_ML.value"),
            _.get(vehicleVM, "collisionSymbol_ML.value"),
            _.get(vehicleVM, "costNew.value"),
            _.get(vehicleVM, "trim_ML.value")
        );

        showProgressDialog(platformMessages.loading);
        try {
            const vehicleInfo = await MLVehicleInfoLookupService.lookUpCollisionCompSymbol(req, authHeader);
            if (vehicleInfo.comprehensiveSymbol_ML && vehicleInfo.collisionSymbol_ML) {
                _.set(vehicleVM, "comprehensiveSymbol_ML.value", vehicleInfo.comprehensiveSymbol_ML);
                _.set(vehicleVM, "collisionSymbol_ML.value", vehicleInfo.collisionSymbol_ML);
                _.set(vehicleVM, "hasSymbolsFilledByCostNew_Ext.value", true);
            }
        } finally {
            hideProgressDialog();
        }
    };

    const checkForDuplicateVins = (vin) => {
        let vinCount = 0;
        if (vin) {
            vehicleList.forEach((veh) => {
                if (veh.value.vin === vin) {
                    vinCount++;
                }
            });
        }
        return vinCount > 1;
    };

    const checkIfVinIsValid = () => {
        updateVinErrorCheck(undefined);
        const invalidVinChar = translator(messages.vinInvalidCharactersError);
        const ninthCharError = translator(messages.vinNinthCharacterError);
        const tenthCharError = translator(messages.vinTenthCharacterError);
        
        const vinValue = (vehicleVM?.vin?.value || "").toUpperCase();
        const ninthChar = vinValue.charAt(8);
        const tenthChar = vinValue.charAt(9);


        const shouldValidate = vinValue.length === 17 && Number(vehicleVM?.year?.value) >= 1981;
        if (!shouldValidate) {
            return true;
        }
        if (/[IOQ]/.test(vinValue)) {
            updateVinErrorCheck(invalidVinChar);
            _.set(vehicleVM, "value.error.message", translator(invalidVinChar));
            updateCustomAlerts([...customAlerts.filter((alert) => alert.block !== "vinErrorCheck"), {
                type: "error",
                message: invalidVinChar,
                block: "vinErrorCheck"
            }])
        }
        else if (!/^[0-9X]$/.test(ninthChar)) {
            updateVinErrorCheck(ninthCharError);
            _.set(vehicleVM, "value.error.message", translator(ninthCharError));
            updateCustomAlerts([...customAlerts.filter((alert) => alert.block !== "vinErrorCheck"), {
                type: "error",
                message: ninthCharError,
                block: "vinErrorCheck"
            }])
        }
        else if (['U', 'Z', '0'].includes(tenthChar)) {
            updateVinErrorCheck(tenthCharError);
            _.set(vehicleVM, "value.error.message", translator(tenthCharError));
            updateCustomAlerts([...customAlerts.filter((alert) => alert.block !== "vinErrorCheck"), {
                type: "error",
                message: tenthCharError,
                block: "vinErrorCheck"
            }])
        }
        if(vinErrorCheck){
            return false
        }
        return true
    }

    const verifyVIN = useCallback(async () => {
        if (!verifyVinInProgress.current) {
            setReadOnly(true);
            verifyVinInProgress.current = true;
            setIsModifierSyncEnabled(false);
            if (isFullVin()) {
                const duplicateVins = checkForDuplicateVins(vehicleVM.vin.value);
                try {
                    // for Add Vehicle Popup
                    if (setBannerMessage) setBannerMessage(null);

                    showProgressDialog(platformMessages.loading);

                    const req = {
                        vin: vehicleVM.vin.value,
                        vehCategory: hasVehCategory() ? vehicleVM.category_ML.value.code : undefined,
                        vehType: hasVehType() ? vehicleVM.type_ML.value.code : undefined,
                        quoteID: getQuoteID(),
                        isCompRaterQuote: isCompRaterQuote,
                        vehFixedId: vehicleVM.fixedId.value
                    };
                    // Set this to false by default
                    _.set(vehicleVM, "hasSymbolsFilledByCostNew_Ext.value", false);
                    let vehicleInfo;

                    try {
                        vehicleInfo = await MLVehicleInfoLookupService.verifyVin(req, authHeader);
                    } catch (error) {
                        if (error.baseError && error.baseError.indexOf('ConcurrentDataChangeException') > -1) {
                            return;
                        }
                        throw error;
                    }

                    let hasVendorAnnualMileage = null;
                    if (vehicleInfo && isPrivatePassenger() && hasCompCollSymbolsNoYear(vehicleInfo) && !hasErrorCodes(vehicleInfo)) {
                        setValidVIN(true);
                        mapVinResponseToVehicle(vehicleInfo, vehicleVM.value);
                        updateAvailableBodyTypes([{
                            code: vehicleInfo.bodyType,
                            name: getBodyTypeValue(vehicleInfo.bodyType)
                        }]);
                        updateNoYearFromVerifyVin(true);
                        vehicleVM.value.year = undefined;
                        if (vehicleInfo.trim) {
                            vehicleVM.value.isTrimVinConfirmed_Ext = true;
                        } else {
                            vehicleVM.value.isTrimVinConfirmed_Ext = false;
                        }
                        hasVendorAnnualMileage = vehicleVM.hasVendorAnnualMileage_ML.value;
                    } else if (vehicleInfo && vehicleInfo.make && vehicleInfo.model && vehicleInfo.year && !hasErrorCodes(vehicleInfo)) {
                        setValidVIN(true);
                        mapVinResponseToVehicle(vehicleInfo, vehicleVM.value);
                        // If the vehicle symbols have not been set from the VinResponse, fill symbols using CostNew
                        if (!vehicleVM.value.collisionSymbol_ML && !vehicleVM.value.comprehensiveSymbol_ML) {
                            await lookupCollision();
                        }
                        clearVehicleErrorMessage();
                        updateAvailableBodyTypes([{
                            code: vehicleInfo.bodyType,
                            name: getBodyTypeValue(vehicleInfo.bodyType)
                        }]);
                        updateNoYearFromVerifyVin(false);
                        vehicleVM.value.isTrimVinConfirmed_Ext = true;
                        hasVendorAnnualMileage = vehicleVM.hasVendorAnnualMileage_ML.value;
                        // ctrl.duplicateVinErrorMsg();
                        if (vehicleInfo.trim) {
                            vehicleVM.value.isTrimVinConfirmed_Ext = true;
                        } else {
                            vehicleVM.value.isTrimVinConfirmed_Ext = false;
                        }
                    } else {
                        setReadOnly(false);
                        setValidVIN(false);
                        updateNoYearFromVerifyVin(false);
                        vehicleVM.value.isTrimVinConfirmed_Ext = false;
                        handleLookupVinError(vehicleInfo);
                    }
                    if (hasVendorAnnualMileage) {
                        vehicleVM.hasVendorAnnualMileage_ML.value = hasVendorAnnualMileage;
                    }
                    if (isPolicyChange && isPolicyChange === true) {
                        if (!duplicateVins) {
                            clearBlockAlerts('duplicateVin');
                        }
                    }
                    if(!isPolicyChange){
                        checkIfVinIsValid();
                        if(vinErrorCheck) return false
                    }
                } catch (error) {
                    setReadOnly(false);
                    setValidVIN(false);
                    handleLookupVinError();
                } finally {
                    hideProgressDialog();
                    setIsModifierSyncEnabled(true);
                    verifyVinInProgress.current = false;
                    if (updateModifiers) updateModifiers();  // is this necessary even verifyVin failed?
                    setVinLiveRegionMessage("Successfully added VIN")
                }
            } else {
                setReadOnly(false);
                setValidVIN(false);
                setIsModifierSyncEnabled(true); // is this necessary?
                verifyVinInProgress.current = false;
            }
        }
    }, [authHeader, checkForDuplicateVins, checkIfVinIsValid, clearBlockAlerts, clearVehicleErrorMessage, getBodyTypeValue, getQuoteID, handleLookupVinError, hasCompCollSymbolsNoYear, hasErrorCodes, hasVehCategory, hasVehType, hideProgressDialog, isCompRaterQuote, isFullVin, isPolicyChange, isPrivatePassenger, lookupCollision, mapVinResponseToVehicle, setBannerMessage, showProgressDialog, updateModifiers, vehicleVM, vinErrorCheck]);

    const setVehicleTypes = () => {
        const vehicleTypeList = vehicleVM.type_ML.aspects.availableValues;
        const types = vehicleTypeList.filter((vehicleType) => {
            return vehicleType.belongsToCategory(vehicleVM.category_ML.value);
        });

        const typecodes = types.map((type) => ({
            code: type.code,
            name: translator({
                id: type.name,
                defaultMessage: type.name
            })
        }));

        updateVehicleTypes(typecodes);
    };

    const onCategorySelect = (value, path) => {
        const oldCategoryCode = _.get(vehicleVM, "category_ML.code.value");
        if (value && value !== oldCategoryCode) {
            _.set(vehicleVM, path, value);
            clearVehicleFields();
            setValidVIN(false); // need verify vin again
            setVehicleTypes();
            setReadOnly(false);

            if (isModifierSyncEnabled && updateModifiers) {
                const updatePromise = updateModifiers(null, true);
                updatePromise.then(() => {
                    // Must wait until after modifiers are synced or value will be set to 0 from the DTO
                    _.set(vehicleVM, 'value_ML.amount', undefined);
                }, (error) => {
                    // eslint-disable-next-line no-console
                    console.error(error);
                });
            }
            if (vehicleVM.value_ML) {
                _.set(vehicleVM, 'value_ML.amount', undefined);
            }
        }
    };

    useEffect(() => {
        const initiateAvailableMakeAndModel = async () => {
            const makeCodes = await fetchMakes(vehicleVM);
            if (makeCodes.length > 0) {
                updateAvailableMakes(makeCodes);
            }
            const modelCodes = await fetchModels(vehicleVM);
            updateAvailableModels(modelCodes);
        };

        //
        // initialize vehicle category
        //
        const initVehicleCategories = () => {
            const policyState = _.get(submissionVM, 'baseData.policyAddress.state.value.code')

            let categories;

            if (policyState === 'NH') {
                categories = vehicleVM.category_ML.aspects.availableValues.filter((value) => {
                    return (value.code !== 'registered_golf_cart' && !value.code.endsWith('_np'));
                });
            } else if(isModernProductPersonalAuto){
                categories = vehicleVM.category_ML.aspects.availableValues.filter((value) => {
                    return value.code.endsWith('_np');
                });
            } else {
                categories = vehicleVM.category_ML.aspects.availableValues.filter((value) => {
                    return (value.code !== 'Other_Personally_Registered' && value.code !== 'registered_golf_cart' && !value.code.endsWith('_np'));
                });
            }

            const categoryCodes = categories.map((category) => ({
                code: category.code,
                name: translator({
                    id: category.name,
                    defaultMessage: category.name
                })
            }));

            updateVehicleCategories(categoryCodes);

            if (isGPA || ((isAMP || isQnB) && _.get(vehicleVM, 'category_ML.value.code'))) {
                setVehicleTypes();
            }
        };

        const initYears = () => {
            const currentYear = new Date().getUTCFullYear();
            const { oldestCarYear } = appConfig.personalAutoConfig;
            // _.range is not inclusive. Need to add -1 to get correct years
            const yearRange = _.range(currentYear + 1, oldestCarYear - 1);
            return yearRange.map(_.toString).map((year) => ({
                code: year,
                name: year
            }));
        };

        initVehicleCategories();
        updateYears(initYears());

        const isVinValid = isNewVehicleTab ? false : (_.get(vehicleVM, "isTrimVinConfirmed_Ext.value") === true || isPartialVinValid());
        setValidVIN(isVinValid);
        setReadOnly(isVinValid || isPrefillFullVinVerified());
        // valid full vin has already been verified in previous page
        _.set(vehicleVM, "isCostNewRequired_ML.value", !isVinValid);

        if (!isVinValid) {
            initiateAvailableMakeAndModel();
        }

        if (isGPA) {
            if (hasVin() && isVehicleYearPost1980()) {
                if (isModernProduct && vehicleVM.populatedByPrefill_ML.value && !isVinVerified()) {
                    // only verify prefilled vin when entering edit vehicle page
                    verifyVIN();
                } else if (!isModernProduct && vehicleVM.populatedByPrefill_ML.value && !isPrefillFullVinVerified()) {
                    verifyVIN();
                }
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    //
    // display
    //

    const showDropdownMake = () => ((isPrivatePassenger() || isCollectible()) && (!hasYear() || (isVehicleYearPost1980() && (availableMakes && availableMakes.length > 0)))) && !readOnly;
    const showDropdownModel = () =>
        !isLowSpeedVehicleGolfCartOrTrailer() && !isOtherPersonallyRegistered() && !isTrailerNPorCamperNp() &&
        !isUnlistedModel && !readOnly && !noYearFromVerifyVin &&
        ((availableMakes && availableMakes.length > 0) || _.isUndefined(vehicleVM.year.value)) && !isVehicleYearPre1981();

    const showBodyTypeAndTrimFields = () => isPrivatePassengerOrCollectiblePre1981() || validVIN || isUnlistedModel;
    const showDropdownBodyType = () => !isLowSpeedVehicleGolfCartOrTrailer() && !isTrailerNPorCamperNp() && !isOtherPersonallyRegistered() && (!readOnly || isVehicleYearPre1981());
    const showTrimInputField = () => {
        return isUnlistedModel || isVehicleYearPre1981() || availableTrims.length === 0;
    };

    const isAgentLookupVinRequired = () => {
        return (isPrivatePassenger() || isCollectible()) && ((!vehicleVM.year.value || vehicleVM.year.value > 1980))
            && !(isVinFullAndValid() || isPrefillFullVinVerified()) && !isUnlistedModel;
    };

    const agentLookupVinWithPopup = async () => {
        const getCleanVinTrims = (vinPrefixes) => {
            return vinPrefixes.filter((prefix) => !_.isEmpty(prefix.trim));
        };

        const selectVinPrefix = (selectedVin) => {
            if (vehicleVM.vintellFields_ML) {
                vehicleVM.vintellFields_ML.value = selectedVin;
            }
            vehicleVM.bodyType_ML.value = selectedVin?.bodyType;
            vehicleVM.trim_ML.value = selectedVin?.trim;
            vehicleVM.vin.value = selectedVin?.vinPrefix;

            if (!_.isEmpty(selectedVin?.collSymbol) && !_.isNil(selectedVin?.collSymbol)) {
                vehicleVM.collisionSymbol_ML.value = selectedVin?.collSymbol;
            }

            if (!_.isEmpty(selectedVin?.compSymbol) && !_.isNil(selectedVin?.compSymbol)) {
                vehicleVM.comprehensiveSymbol_ML.value = selectedVin?.compSymbol;
            }


            // no reset on readonly
            setValidVIN(true);
            clearVehicleErrorMessage();

            if (isModernProductPersonalAuto) {
                vehicleVM.fuelType_ML.value = selectedVin?.fuelCode;
            }

            refreshData();

            if (updateModifiers) {
                updateModifiers();
            }
        };

        const vinPrefixes = await lookupVIN();
        if (vinPrefixes && vinPrefixes.length > 0) {
            vinPrefixes.forEach((vinVehicle) => {
                // select indicator
                vinVehicle.checked = false;
            });
        }

        const vehiclesInfo = getCleanVinTrims(vinPrefixes);

        modalApi.showModal(
            <FormModalPopover
                title={SelectVehiclePopupMessage.selectVehicleTitle}
                viewModel={vehiclesInfo}
                CustomComponent={SelectVehiclePopupComponent}
                actionBtnLabel={platformMessages.mlSave}
                buttonType="outlined"
                cancelBtnLabel={platformMessages.mlCancel}
                showFooterOnCustomComponent
            />
        ).then(() => {
            if (!_.isEmpty(vehiclesInfo)) {
                const selectedVin = vehiclesInfo.find((v) => v.checked);
                selectVinPrefix(selectedVin);

                const value = _.get(vehicleVM, "costNew.amount.value");

                if (_.isEmpty(selectedVin?.collSymbol) && _.isEmpty(selectedVin?.compSymbol) && value !== 0 && value !== null && !_.isUndefined(value)){

                    lookupCollision();
                }

            }
            if (onValidate) {
                onValidate(isComponentValid, id);
            }
        });
    };

    const updateVehicleMakeCodeML = () => {
        const make = vehicleVM.make.value;
        if (isPropertyDefinedAndNotEmpty(make, vehicleVM.value, 'make')) {
            fetchModels(vehicleVM);
        }
        if (isLowSpeedVehicleGolfCartOrTrailer() || isPrivatePassengerOrCollectiblePre1981() || isTrailerNPorCamperNp()) {
            vehicleVM.makeCode_ML.value = make;
        }

        if (!showDropdownModel() && !_.get(vehicleVM, 'value.isVinValid')) {
            vehicleVM.makeCode_ML.value = make;
        }
    };

    const setModelCode = () => {
        vehicleVM.modelCode_ML.value = getModelCode(availableModels, vehicleVM.model.value) || vehicleVM.modelCode_ML.value;
    };

    const getAndSetTrims = async () => {
        updateAvailableTrims([]);
        const vinPrefixes = await lookupVIN();
        assignTrims(vinPrefixes);
    };

    const updateModelCodeML = () => {
        const model = vehicleVM.model.value;
        if (isPropertyDefinedAndNotEmpty(model, vehicleVM.value, 'model')) {
            setModelCode();
            getAndSetTrims();
        } else {
            _.set(vehicleVM, 'model.value', undefined);
            _.set(vehicleVM, 'modelCode_ML.value', undefined);
        }

        if (!showDropdownModel() && !_.get(vehicleVM, 'value.isVinValid')) {
            vehicleVM.modelCode_ML.value = model;
        }
    };

    const getValueTooltip = useCallback(() => {
        const policyState = _.get(submissionVM, 'baseData.policyAddress.state.value.code')
        if (policyState === 'AZ') {
            return messages.valueToltipContentAZ;
        }
        if (policyState === 'NH') {
            return messages.valueToltipContentNH;
        }
        return messages.valueToltipContentRest;
    }, [submissionVM]);

    const vinValid = () => {
        return validVIN && !!vehicleVM.value.vin;
    };

    const updateVin = useCallback((value) => {
        setReadOnly(false);
        setValidVIN(false);
        clearVinFields(vehicleVM);
    }, [clearVinFields, vehicleVM]);

    const onFocusVinNumber = useCallback(() => {
        setPriorVinInput(vehicleVM.vin.value);
    }, [vehicleVM.vin.value]);

    const onBlurVinNumber = useCallback(() => {
        const newValue = vehicleVM.vin.value;
        if (newValue !== priorVinInput) {
            setVinModified(true);
            updateVin(newValue);
        }
    }, [priorVinInput, updateVin, vehicleVM.vin.value]);

    const showInputBodyType = () => {
        return !isLowSpeedVehicleGolfCartOrTrailer() &&!isTrailerNPorCamperNp() && !isOtherPersonallyRegistered() && readOnly;
    };

   const getSearchTooltipContent = () => {
   if (isQnB) {
    return messages.addTrailerTooltipContentQb;
       }
       
  if (isGPA) {
    return messages.addTrailerTooltipContent;  
       }
       
  return undefined;

    };
    
    const currentYear = new Date().getFullYear();

    const isVehicleUsedOrNew = (value) => {
        if (value >= currentYear - 1) {
            setShowOwnedLessThan1Year(true);
        }
        else {
            setShowOwnedLessThan1Year(false);
        }
    }

    const checkVehicleCategory = (value) => {
        if (value === 'trailer_np' || value === 'camper_np') {
            setIsTrailerOrCamper(true);
        }
        else {
            setIsTrailerOrCamper(false);
        }
    }

    const showValueField = useMemo(() => {
        return isModernProductPersonalAuto
            ? isVehicleYearPre1981()
            : (showAdditionalFields && !isPrivatePassenger());
    }, [
        isModernProductPersonalAuto,
        showAdditionalFields,
        isVehicleYearPre1981, 
        isPrivatePassenger, 
    ]);

    const showCostField = () => {
        return !isPolicyChange && showAdditionalFields && isPrivatePassenger() && isCostNewVisible();
    };

    const lockCostNew = () => {
        return isFullVin() && isValidVin() && hasCostNew();
    }

    const getDefaultVehicleCategory = () => {
        return isModernProductPersonalAuto ? 'private_passenger_np' : 'private_passenger_light_truck';
    }

    const overrideProps = {
        categoryRow:{
            className: isQnB ? "rowContainer qnbFieldForToolTip" : "rowContainer"
        },
        typeRow: {
            className: isQnB ? "rowContainer qnbFieldForToolTip" : "rowContainer"
        },
        vehicleCategoryList: {
            availableValues: vehicleCategories,
            defaultValue: getDefaultVehicleCategory(),
            onValueChange: (value, path) => {
                if (_.isEmpty(value)) {
                    writeValue(value, path);
                    return;
                }
                onCategorySelect(value, path);
                checkVehicleCategory(value);
            },
            tooltip: {
                title: isGPA ? messages.addTrailer : undefined,
                text: getSearchTooltipContent()
            }
        },
        vehicleCategoryTooltip: {
            visible: !isGPA,
            text: getSearchTooltipContent()
        },
        // same as agent
        vehicleTypeList: {
            availableValues: vehicleTypes,
            visible: !isRegisteredGolfCart() && !isOtherPersonallyRegistered(),
            required: (!isRegisteredGolfCart() && !isOtherPersonallyRegistered()) && isQnB ? true : vehicleVM.type_ML.aspects.required,
            value: _.get(vehicleVM, 'type_ML.value'),
            onValueChange:  async (value, path) => {
               if (_.isEmpty(value)) {
                    writeValue(value, path);
                    return;
                }

                writeValue(value, path);

                if (typeof setFarmUseField === 'function') {
                    await setFarmUseField(value?.code);
              }
            } 
        },
        // same as agent
        vehicleTypeInput: {
            visible: isOtherPersonallyRegistered(),
            disabled: readOnly
        },
        vehicleTypeTooltip: {
            visible: !isGPA
        },
        // same as agent
        vehicleYearList: {
            availableValues: years,
            onValueChange: async (value, path) => {
                if (_.isEmpty(value)) {
                    writeValue(value, path);
                    return;
                }
                await onYearSelect(value, path);
                isVehicleUsedOrNew(value);
            },
            visible: !readOnly
        },
        // same as agent
        vehicleYearInput: {
            visible: readOnly,
            disabled: readOnly
        },
        // sane as agent
        vehicleMakeList: {
            availableValues: availableMakes,
            onValueChange: async (value, path) => {
                if (_.isEmpty(value)) {
                    writeValue(value, path);
                    return;
                }
                await onMakeSelect(value, path);
            },
            visible: showDropdownMake()
        },
        // same as agent
        vehicleMakeInput: {
            visible: !showDropdownMake(),
            disabled: readOnly,
            onBlur: updateVehicleMakeCodeML
        },
        // same as agent
        vehicleModelList: {
            availableValues: availableModels,
            visible: showDropdownModel(),
            onValueChange: async (value, path) => {
                if (_.isEmpty(value)) {
                    writeValue(value, path);
                    return;
                }
                await onModelSelect(value, path);
            }
        },
        // same as agent
        vehicleModelInput: {
            visible: !showDropdownModel(),
            disabled: readOnly,
            onValueChange: onModelInputChange,
            onBlur: updateModelCodeML
        },
        // same as agent
        vehicleModelCheckbox: {
            visible: !isLowSpeedVehicleGolfCartOrTrailer() && !isOtherPersonallyRegistered() && !readOnly && !isVehicleYearPre1981() && !isTrailerNPorCamperNp(),
            onValueChange: async (value) => {
                setIsUnlistedModel(value);
                _.set(vehicleVM, 'isUnlistedModel', value);
                if (value) {
                    updateFullBodyTypeList();
                    updateAvailableTrims([]);
                }
                // clear bodyType and trim values
                clearVehicleFieldsAfterModel();
                vehicleVM.vin.value = undefined;
                setValidVIN(false);
            },
            className: isQnB ? "radioButtonQnB" : "",
            value: isUnlistedModel
        },
        vehicleBodyTypeList: {
            visible: showDropdownBodyType() && !isModernProductPersonalAuto,
            disabled: validVIN && hasBodyType()
        },
        vehicleBodyTypeInput: {
            value: hasBodyType() ? getBodyTypeValue(vehicleVM.value.bodyType_ML) : '',
            visible: showInputBodyType() && !isModernProductPersonalAuto,
            disabled: readOnly
        },
        vehicleTrimTypeList: {
            availableValues: availableTrims,
            visible: !showTrimInputField(),
            disabled: validVIN
        },
        vehicleTrimTypeInput: {
            visible: !isLowSpeedVehicleGolfCartOrTrailer() && showTrimInputField() && !isOtherPersonallyRegistered() && !isTrailerNPorCamperNp(),
            disabled: (readOnly && vehicleVM.isTrimVinConfirmed_Ext.value)
        },
        vinContainer: {
            visible: !isGPA && !isLowSpeedVehicleGolfCartOrTrailer() && !isTrailerNPorCamperNp() && !isOtherPersonallyRegistered() && (!vehicleVM.year.value || vehicleVM.year.value > 1980)
        },
        // same as agent
        vinNumberInput: {
            onFocus: onFocusVinNumber,
            onBlur: onBlurVinNumber,
            className: `${isQnB ? 'vinInputQnB' : 'vinInput'}`
        },
        addVINLiveRegion: {
            content: isQnB ? vinLiveRegionMessage : ""
        },
        addMyVinButton: {
            onClick: () => {
                updateSearchVehicleWithVin(true);
            },
            visible: !searchVehicleWithVin,
            className: isQnB ? "addMyVinButton secondaryButton" : "addMyVinButton",
            content: isQnB ? translator(messages.addwithVINBtn) : translator(messages.addwithVIN)
        },
        vinText: {
           content: isQnB ? messages.simplyVINQb: messages.simplyVIN 
        },
        vinTextTooltip: {
            tooltipHeader:isQnB ? messages.vinhelpTextHeaderQb : messages.vinhelpTextHeader
        },
        vinRow: {
            visible: searchVehicleWithVin
        },
        lookUpVinButton: {
            onClick: () => verifyVIN(),
            disabled: readOnly || !isFullVin(),
            className: isQnB ? "lookupVinBtn accentButton widthSmall" : "lookupVinBtn",
            content: isQnB ? translator(messages.lookupVinBtn) : translator(messages.LOOKUPVINBtn)
        },
        validVinIcon: {
            visible: readOnly
        },
        cancelVinButton: {
            onClick: () => {
                setValidVIN(false);
                updateSearchVehicleWithVin(false);
                vehicleVM.year = undefined;
                vehicleVM.vin = undefined;
                clearVehicleFieldsAfterYear();
                updateAvailableBodyTypes([]);
            }
        },

        // agent updates
        agentVerifyVinRow: {
            visible: !isLowSpeedVehicleGolfCartOrTrailer() && !isOtherPersonallyRegistered() && !isTrailerNPorCamperNp()
        },
        agentVerifyVinIcon: {
            visible: (isPrivatePassenger || isCollectible) && (vinValid() || isPrefillFullVinVerified()) && isFullVin()
        },
        verifyVinButton: {
            disabled: !isFullVin() || (isModernProductPersonalAuto && (isPrivatePassenger || isCollectible) && (vinValid() || isPrefillFullVinVerified()) && isFullVin())
        },
        agentLookupVinContainer: {
            visible: isAgentLookupVinRequired()
        },
        agentLookUpVinButton: {
            disabled: !canLookupVin(),
            'aria-description': (canLookupVin() && isAgentLookupVinRequired()) ? 'This is required' : ''
        },
        agentbodyTypeTrimRow: {
            visible: showBodyTypeAndTrimFields()
        },
        agentVehicleBodyTypeList: {
            visible: (showDropdownBodyType() && !isVehicleYearPre1981() && !showInputBodyType()) && !isUnlistedModel && !isModernProductPersonalAuto,
            disabled: validVIN && hasBodyType()
        },
        agentBodyTypeListUnlistedModelOrPre1981: {
            visible: ((showDropdownBodyType() && isVehicleYearPre1981() && !showInputBodyType()) || isUnlistedModel) && !isModernProductPersonalAuto,
            disabled: readOnly
        },
        agentVehicleBodyTypeInput: {
            value: hasBodyType() ? getBodyTypeValue(vehicleVM.value.bodyType_ML) : '',
            visible: showInputBodyType() && !isModernProductPersonalAuto,
            disabled: readOnly
        },
        agentVehicleTrimTypeList: {
            availableValues: availableTrims,
            visible: !showTrimInputField(),
            disabled: validVIN && hasTrim()
        },
        agentVehicleTrimTypeInput: {
            visible: !isLowSpeedVehicleGolfCartOrTrailer() && showTrimInputField() && !isTrailerNPorCamperNp(),
            disabled: (readOnly || vehicleVM.isTrimVinConfirmed_Ext.value)
        },
        agentSerialNumberRow: {
            visible: isLowSpeedVehicleGolfCartOrTrailer() || isOtherPersonallyRegistered()
        },
        agentLookupVinIcon: {
            visible: (isPrivatePassenger() || isCollectible()) && isPartialVinValid() && !isPrefillFullVinVerified()
        },
        agentVinRquiredHelpText: {
            visible: (isPrivatePassenger() || isCollectible()) && !isPartialVinValid() && hasYear()
        },
        // Cost field for quote
        agentQuoteCostNewField: {
            visible: showCostField(),
            disabled: lockCostNew(),
            required: !lockCostNew(),
            onBlur: () => {
                const value = _.get(vehicleVM, "costNew.amount.value");

                if (_.isUndefined(value)) {
                    vehicleVM.costNew.amount = undefined;
                    // Call lookupcollision if vin is selected from lookup and call,comp symbols are null or empty.
                } else if ((!vehicleVM.value.compCollFilledByVinML || (_.isEmpty(vehicleVM?.value?.collSymbol) && _.isEmpty(vehicleVM?.value?.compSymbol))) && value !== 0 && value !== null) {
                    lookupCollision();
                }
            }
        },
        agentComprehensiveDiv: {
            visible: showAdditionalFields && isPrivatePassenger() && !_.isUndefined(_.get(vehicleVM, "comprehensiveSymbol_ML.value"))
        },
        agentCollisionDiv: {
            visible: showAdditionalFields && isPrivatePassenger() && !_.isUndefined(_.get(vehicleVM, "collisionSymbol_ML.value"))
        },
        agentComprehensiveField: {
            visible: !isModernProductPersonalAuto,
            content: <FormattedMessage
                {...messages.labelValuePlaceholder}
                values={{
                    label: (<strong className="mlFieldLabel">{translator(messages.comprehensiveSymbol)}</strong>),
                    value: _.get(vehicleVM, 'comprehensiveSymbol_ML.value', '')
                }}
            />,
        },
        agentCollisionField: {
            visible: !isModernProductPersonalAuto,
            content: <FormattedMessage
                {...messages.labelValuePlaceholder}
                values={{
                    label: (<strong className="mlFieldLabel">{translator(messages.collisionSymbol)}</strong>),
                    value: _.get(vehicleVM, 'collisionSymbol_ML.value', '')
                }}
            />,
        },
        agentValueField: {
            visible: showValueField,
            tooltip: {
                id: getValueTooltip().id,
                text: getValueTooltip().defaultMessage
            }
        },
        invalidVinError: {
            visible: !_.isNil(_.get(vehicleVM, "value.error.message")) && !showAdditionalFields,
            messageProps: {
                error: _.get(vehicleVM, "value.error.message", "")
            }
        },
        invalidCommercialVinError: {
            visible: !_.isUndefined(vinError) && !showAdditionalFields,
            messageProps: {
                error: vinError
            }
        },
        invalidCommercialVinErrorPolChange: {
            visible: !_.isUndefined(vinError) && showAdditionalFields,
            messageProps: {
                error: vinError
            }
        },
        ineligibleVinError: {
            visible: !_.isUndefined(vinError) && !showAdditionalFields,
            messageProps: {
                error: vinError
            }
        },
        agentEditVehicleVinError: {
            visible: !_.isUndefined(vinError) && showAdditionalFields,
            messageProps: {
                error: vinError
            }
        },
        replacingVehicleNotification: {
            visible: !!vehicleVM.value.isReplacing,
            messageProps: {
                success: <FormattedMessage
                    {...messages.vehicleReplaced}
                    values={{
                        strongName: <strong>{vehicleVM.value.orgVehicleName}</strong>
                    }}
                />
            }
        },
        invalidVinErrorCharacter: {
            visible: !!vinErrorCheck,
            messageProps: {
                error: vinErrorCheck
            }
        }
    };

    useEffect(() => {
        if (onValidate) {
            onValidate(isComponentValid, id);
        }
    }, [id, onValidate, isComponentValid]);

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onValidate,
            onVerifyVIN: verifyVIN,
            onAgentLookupVin: agentLookupVinWithPopup
        },
        resolveComponentMap: {
            MLAlertGroupComponent
        }
    };

    return (
        <ViewModelForm
            uiProps={isGPA ? agentMetadata.pageContent : metadata.pageContent}
            model={vehicleVM}
            overrideProps={overrideProps}
            onValueChange={writeValue}
            classNameMap={resolvers.resolveClassNameMap}
            onValidationChange={setComponentValidation}
            showErrors={showValidationErrors}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
        />
    );
}

MLPartialVehicleDetailsComponent.propTypes = {
    viewModel: PropTypes.shape({}).isRequired,
    submissionVM: PropTypes.shape({}).isRequired,
    updateModifiers: PropTypes.func,
    updateWizardData: PropTypes.func,
};

MLPartialVehicleDetailsComponent.defaultProps = {
    updateModifiers: undefined,
    updateWizardData: (value) => { },
};

export default MLPartialVehicleDetailsComponent;